"use client";

import React, { useCallback, useEffect, useState } from "react";

import useEmblaCarousel from "embla-carousel-react";

import Section from "@/components/common/Section/Section";
import {
  SliderArrowNext,
  SliderArrowPrevious,
} from "@/components/common/Slider/Controls/SliderArrows";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { cn } from "@/utils/general";

import QuoteSlider2Card, { type QuoteSlider2Item } from "./QuoteSlider2Card";

const STEP = 3;
const ARROW_CLASS =
  "inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-0 bg-white text-nova-base-black shadow-[0_0_20px_rgb(0_0_0_/_5%),0_24px_38px_rgb(0_0_0_/_3%),0_9px_46px_rgb(0_0_0_/_3%),0_11px_15px_rgb(0_0_0_/_3%)] md:h-14 md:w-14 [&_svg]:h-3 [&_svg]:w-3 md:[&_svg]:h-3.5 md:[&_svg]:w-3.5 disabled:cursor-not-allowed disabled:opacity-35";

export type QuoteSlider2Data = {
  title: string;
  disclaimer: string;
  items: QuoteSlider2Item[];
};

type QuoteSlider2Props = {
  data: QuoteSlider2Data;
};

export default function QuoteSlider2({ data }: QuoteSlider2Props) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 2,
    breakpoints: { "(min-width: 1200px)": { slidesToScroll: 3 } },
  });
  const [visibleCount, setVisibleCount] = useState(STEP);
  const [showArrows, setShowArrows] = useState(false);
  const canLoadMore = data.items.length > visibleCount;

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const updateArrows = useCallback(() => {
    if (!emblaApi) return;
    setShowArrows(emblaApi.canScrollNext() || emblaApi.canScrollPrev());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit();
    updateArrows();
    emblaApi.on("reInit", updateArrows);
    return () => {
      emblaApi.off("reInit", updateArrows);
    };
  }, [emblaApi, data.items.length, updateArrows]);

  return (
    <Section maxWidth={1520} px="px-6" py="mb-[34px] py-0 lg:mb-[65px] xl:mb-[81px]">
      <TextElementNova
        element="h3"
        text={data.title}
        className="Title-20-Super-Normal text-center"
      />
      <div className="relative mt-10 hidden md:block lg:mt-16">
        {showArrows ? (
          <SliderArrowPrevious
            scrollPrev={scrollPrev}
            disabled={!emblaApi}
            arrowButtonClassName={cn(
              ARROW_CLASS,
              "absolute left-[-20px] top-1/2 z-10 -translate-y-1/2 md:left-[-28px]"
            )}
          />
        ) : null}
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {data.items.map((item) => (
              <div
                key={item.name}
                className="min-w-0 flex-[0_0_100%] pr-2 md:flex-[0_0_50%] min-[1200px]:flex-[0_0_33.333%]"
              >
                <QuoteSlider2Card item={item} />
              </div>
            ))}
          </div>
        </div>
        {showArrows ? (
          <SliderArrowNext
            scrollNext={scrollNext}
            disabled={!emblaApi}
            arrowButtonClassName={cn(
              ARROW_CLASS,
              "absolute right-[-12px] top-1/2 z-10 -translate-y-1/2 md:right-[-20px]"
            )}
          />
        ) : null}
      </div>
      <div className="relative mt-10 flex flex-col gap-2 md:hidden">
        {data.items.slice(0, visibleCount).map((item) => (
          <QuoteSlider2Card key={item.name} item={item} />
        ))}
        {canLoadMore ? (
          <span className="absolute bottom-0 w-full bg-gradient-to-b from-transparent to-nova-base-white pt-[100px] text-center">
            <button
              type="button"
              className="border-b border-nova-brand-primary-80 pb-[3px] font-dinamoMono text-tiny text-nova-base-black"
              aria-label={`Show ${data.items.length - visibleCount} more quotes`}
              onClick={() => setVisibleCount((count) => count + STEP)}
            >
              +{data.items.length - visibleCount} More
            </button>
          </span>
        ) : null}
      </div>
      <TextElementNova
        element="p"
        text={data.disclaimer}
        className="pt-10 text-center text-nova-transparency-black-70"
        classes={{ default: "Caption-10-Dia-Normal", 1024: "Body-20-Dia-Normal" }}
      />
    </Section>
  );
}
